import { post, authFetch } from '../request';
import type { Asset } from '@/types';

/** 归一化后的素材项（后端 AssetRecord 字段名对齐前端展示）。 */
export interface AssetItem {
  id: string;
  url: string;
  thumbnail?: string;
  fileName?: string;
  name?: string;
  slotName?: string;
  category?: string;
  resourceType?: 'image' | 'animation' | 'audio' | 'particle';
  type?: 'image' | 'audio';
  source?: 'ai_generated' | 'user_upload' | 'user_replaced' | 'public_library' | 'my_library';
  width?: number;
  height?: number;
  fileSize?: number;
  duration?: number;
  /** 生成失败标记（前端用） */
  error?: boolean;
  errorMessage?: string;
  retryable?: boolean;
  /** 落盘时间（同槽位多版本排序用） */
  createdAt?: string;
}

export interface ImageProviderStatuses {
  defaultProvider: 'jimeng' | 'wanxiang';
  jimeng: {
    installed: boolean;
    authenticated: boolean;
    available: boolean;
    version?: string;
    credit?: number;
    message?: string;
  };
  wanxiang: {
    configured: boolean;
    available: boolean;
    message?: string;
  };
}

export interface JimengLoginFlow {
  authenticated: boolean;
  pending?: boolean;
  code?: string;
  verificationUri?: string;
  userCode?: string;
  deviceCode?: string;
  expiresAt?: string;
  message?: string;
}

export async function getImageProviderStatuses(refresh = false): Promise<ImageProviderStatuses> {
  const res = await authFetch(`/api/assets/image/providers${refresh ? '?refresh=true' : ''}`);
  if (!res.ok) throw new Error(`GET image providers failed: ${res.status}`);
  return res.json();
}

export function startJimengLogin(): Promise<JimengLoginFlow> {
  return post<JimengLoginFlow>('/api/assets/image/providers/jimeng/login');
}

export function checkJimengLogin(deviceCode: string): Promise<{ authenticated: boolean; pending: boolean; message?: string }> {
  return post('/api/assets/image/providers/jimeng/login/check', { deviceCode });
}

export function logoutJimeng(): Promise<{ success: boolean; message: string }> {
  return post('/api/assets/image/providers/jimeng/logout');
}

/** 后端 AssetRecord.filePath → 前端 url；补全展示字段。 */
function normalizeAsset(r: Record<string, unknown>): AssetItem {
  const filePath = (r.filePath as string) ?? (r.url as string) ?? '';
  const resourceType = (r.resourceType as AssetItem['resourceType'])
    ?? ((r.type as string)?.startsWith('audio') ? 'audio' : 'image');
  return {
    id: r.id as string,
    url: filePath,
    thumbnail: (r.thumbnail as string) ?? filePath,
    fileName: r.fileName as string | undefined,
    name: (r.name as string) ?? (r.fileName as string | undefined),
    slotName: r.slotName as string | undefined,
    category: r.category as string | undefined,
    resourceType,
    type: (r.type as AssetItem['type']) ?? (resourceType === 'audio' ? 'audio' : 'image'),
    source: (r.source as AssetItem['source']) ?? 'ai_generated',
    width: r.width as number | undefined,
    height: r.height as number | undefined,
    fileSize: r.fileSize as number | undefined,
    duration: r.duration as number | undefined,
    error: r.error as boolean | undefined,
    errorMessage: r.errorMessage as string | undefined,
    retryable: r.retryable as boolean | undefined,
    createdAt: r.createdAt as string | undefined,
  };
}

/** 拉取归一化素材列表（GET /api/assets 返回裸数组）。 */
export async function listAssets(params?: {
  resourceType?: string;
  sessionId?: string;
  search?: string;
}): Promise<AssetItem[]> {
  const q = new URLSearchParams();
  if (params?.resourceType) q.set('resourceType', params.resourceType);
  if (params?.sessionId) q.set('sessionId', params.sessionId);
  if (params?.search) q.set('search', params.search);
  const qs = q.toString();
  // DO-NOT-REVERT: BUG-20260731-008 请求必须有超时出口——浏览器连接池被挂起请求
  // 占满时 fetch 会无限排队（后端实测毫秒级，但前端永不定），无超时会永远骨架屏。
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 10000);
  let res: Response;
  try {
    res = await authFetch(`/api/assets${qs ? `?${qs}` : ''}`, { signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) throw new Error(`GET /api/assets failed: ${res.status}`);
  const data = (await res.json()) as unknown;
  const list = Array.isArray(data)
    ? data
    : ((data as { data?: unknown[] })?.data ?? []);
  return (list as Array<Record<string, unknown>>).map(normalizeAsset);
}

/** 资产库分页查询（GET /api/assets/library → {success,data,total}）。 */
export async function getLibraryAssets(params: {
  resourceType?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: AssetItem[]; total: number }> {
  const q = new URLSearchParams();
  if (params.resourceType && params.resourceType !== 'all') q.set('resourceType', params.resourceType);
  if (params.search) q.set('search', params.search);
  q.set('limit', String(params.limit ?? 50));
  q.set('offset', String(params.offset ?? 0));
  const res = await authFetch(`/api/assets/library?${q.toString()}`);
  if (!res.ok) throw new Error(`GET /api/assets/library failed: ${res.status}`);
  const data = (await res.json()) as { success?: boolean; data?: unknown[]; total?: number };
  const items = (data?.data ?? []) as Array<Record<string, unknown>>;
  return { items: items.map(normalizeAsset), total: data?.total ?? 0 };
}

export type AssetScope = 'public' | 'personal';
/**
 * 公共资产分类。
 * 展示 Tab：image / animation / particle / soundEffect / bgm
 * 隐藏分类（数据已入 manifest，Tab 暂不展示）：preset / sceneTemplate / fxAtom / model3d
 */
export type PublicCategory =
  | 'image' | 'particle' | 'animation' | 'soundEffect' | 'bgm'
  | 'preset' | 'sceneTemplate' | 'fxAtom' | 'model3d';
export type PersonalCategory = 'image' | 'animation' | 'audio';
export type LibraryCategory = PublicCategory | PersonalCategory;

export interface CategorizedAssetItem {
  id: string;
  name: string;
  type: 'image' | 'animation' | 'particle' | 'audio';
  url: string;
  previewUrl: string;
  subcategory: string;
  size: number;
  /** 动效序列帧 CDN URL 数组（animation 类型资源携带） */
  frames?: string[];
  /** 素材来源：区分公共库/AI 生成/个人上传，前端据此展示标识 */
  source?: 'public_library' | 'ai_generated' | 'user_upload' | 'user_replaced';
  /** 落库时间（ISO），个人上传项据此倒序置顶 */
  createdAt?: string;
}

/** 分类资产库查询（GET /api/assets/library?scope=&category=）。 */
export async function getCategorizedLibraryAssets(params: {
  scope: AssetScope;
  category: LibraryCategory;
  subcategory?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: CategorizedAssetItem[]; total: number; subcategories: string[] }> {
  const q = new URLSearchParams();
  q.set('scope', params.scope);
  q.set('category', params.category);
  if (params.subcategory && params.subcategory !== 'all') q.set('subcategory', params.subcategory);
  if (params.search) q.set('search', params.search);
  q.set('limit', String(params.limit ?? 100));
  q.set('offset', String(params.offset ?? 0));
  const res = await authFetch(`/api/assets/library?${q.toString()}`);
  if (!res.ok) throw new Error(`GET /api/assets/library failed: ${res.status}`);
  const data = (await res.json()) as {
    success?: boolean;
    data?: CategorizedAssetItem[];
    total?: number;
    subcategories?: string[];
  };
  return {
    items: data?.data ?? [],
    total: data?.total ?? 0,
    subcategories: data?.subcategories ?? [],
  };
}

/** 将资产库资源批量导入到当前会话。 */
export function importLibraryAssets(assetIds: string[], sessionId: string): Promise<{ success: boolean; imported: number }> {
  return post<{ success: boolean; imported: number }>('/api/assets/import-batch', { assetIds, sessionId });
}

// ─────────────── 转场动画库（聊天框"动画"快捷按钮） ───────────────

export type AnimationCategory = 'entrance' | 'exit' | 'loop';

export interface TweenAnimationItem {
  /** 中文名（用于快捷指令拼接与展示） */
  name: string;
  /** 英文 key */
  nameEn: string;
  category: AnimationCategory;
  categoryLabel: string;
  duration: number;
  /** CDN 预览 gif URL */
  preview: string;
}

export interface AnimationGroup {
  category: AnimationCategory;
  label: string;
  count: number;
}

/**
 * 查询转场动画库（进场/退场/循环）。
 * GET /api/playable/animations?category=&q=
 */
export async function getAnimationLibrary(params?: {
  category?: AnimationCategory;
  search?: string;
}): Promise<{ items: TweenAnimationItem[]; groups: AnimationGroup[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.category) q.set('category', params.category);
  if (params?.search) q.set('q', params.search);
  const qs = q.toString();
  const res = await authFetch(`/api/playable/animations${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error(`GET /api/playable/animations failed: ${res.status}`);
  const data = (await res.json()) as {
    success?: boolean;
    animations?: TweenAnimationItem[];
    groups?: AnimationGroup[];
    total?: number;
  };
  return {
    items: data?.animations ?? [],
    groups: data?.groups ?? [],
    total: data?.total ?? 0,
  };
}

/** 替换指定素材文件，返回新素材及可能的重注入预览地址。BUG-20260810-060: newVersion=替换产生的新版本 */
export async function replaceAsset(id: string, file: File, basePlayableId?: string, fallbackMeta?: { slotName?: string; sessionId?: string }): Promise<{ success: boolean; asset: Record<string, unknown>; previewUrl?: string; newVersion?: { id: string; version: number } }> {
  const form = new FormData();
  form.append('file', file);
  // BUG-20260812-078: 历史版本改图——指定时以该版本产物为 fork 源
  if (basePlayableId) form.append('basePlayableId', basePlayableId);
  // BUG-20260812-019④: 快照 id 失效时后端按槽位反查最新记录兑底
  if (fallbackMeta?.slotName) form.append('slotName', fallbackMeta.slotName);
  if (fallbackMeta?.sessionId) form.append('sessionId', fallbackMeta.sessionId);
  const res = await authFetch(`/api/assets/${id}/replace`, { method: 'POST', body: form });
  if (!res.ok) throw new Error(`replace asset failed: ${res.status}`);
  return res.json();
}

/** 用资产库资源（CDN URL）替换指定素材——后端负责下载，规避浏览器跨域。 */
export async function replaceAssetFromUrl(id: string, url: string, basePlayableId?: string, fallbackMeta?: { slotName?: string; sessionId?: string }): Promise<{ success: boolean; asset: Record<string, unknown>; previewUrl?: string; newVersion?: { id: string; version: number } }> {
  const form = new FormData();
  form.append('url', url);
  if (basePlayableId) form.append('basePlayableId', basePlayableId);
  // BUG-20260812-019④: 快照 id 失效时后端按槽位反查最新记录兑底
  if (fallbackMeta?.slotName) form.append('slotName', fallbackMeta.slotName);
  if (fallbackMeta?.sessionId) form.append('sessionId', fallbackMeta.sessionId);
  const res = await authFetch(`/api/assets/${id}/replace`, { method: 'POST', body: form });
  if (!res.ok) throw new Error(`replace asset failed: ${res.status}`);
  return res.json();
}

/**
 * 一键替换：将会话内「已上传/已替换」的图片自动匹配槽位，批量注入最新试玩产物并返回新预览地址。
 */
export async function replaceAllAssets(sessionId: string, assetIds?: string[]): Promise<{
  success: boolean;
  previewUrl?: string;
  replacedSlots?: string[];
  missingSlots?: string[];
  failed?: string[];
  newVersion?: { id: string; version: number };
}> {
  const res = await authFetch('/api/assets/replace-all', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, assetIds }),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new Error(data?.message ?? `replace-all failed: ${res.status}`);
  }
  return res.json();
}

/** 删除指定素材。 */
export async function deleteAsset(id: string): Promise<void> {
  const res = await authFetch(`/api/assets/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`delete asset failed: ${res.status}`);
}

export function uploadAsset(
  file: File,
  options?: { category?: string; sessionId?: string },
): Promise<Asset> {
  const form = new FormData();
  form.append('file', file);
  if (options?.category) form.append('category', options.category);
  if (options?.sessionId) form.append('sessionId', options.sessionId);
  return post<Asset>('/api/assets/upload', form);
}

/** 上传素材并返回 assetId + 访问 URL（POST /api/assets/upload → {success, asset}）。 */
export async function uploadAssetFile(
  file: File,
  options?: { category?: string; slotName?: string; sessionId?: string },
): Promise<{ id: string; url: string }> {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('category', options?.category ?? 'custom');
  if (options?.slotName) form.append('slotName', options.slotName);
  if (options?.sessionId) form.append('sessionId', options.sessionId);
  const res = await authFetch('/api/assets/upload', { method: 'POST', body: form });
  if (!res.ok) throw new Error(`upload failed: ${res.status}`);
  const data = (await res.json()) as { success?: boolean; asset?: { id: string; filePath?: string; url?: string } };
  const asset = data.asset;
  return { id: asset?.id ?? '', url: asset?.filePath ?? asset?.url ?? '' };
}

/** 上传视频（filePath 供生成链路使用，url 供聊天消息预览）。 */
export async function uploadVideo(file: File): Promise<{ filePath: string; fileName: string; url: string; duration: number }> {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await authFetch('/api/agent/video/upload', { method: 'POST', body: form });
  if (!res.ok) throw new Error(`video upload failed: ${res.status}`);
  const data = (await res.json()) as { data?: { filePath: string; fileName: string; url?: string; duration?: number } };
  return {
    filePath: data.data?.filePath ?? '',
    fileName: data.data?.fileName ?? file.name,
    url: data.data?.url ?? '',
    duration: data.data?.duration ?? 0,
  };
}

/** 上传结束页图片（POST /api/agent/video/endcard-upload → {success, data:{filePath, fileName}}）。 */
export async function uploadEndcard(file: File): Promise<{ filePath: string; fileName: string }> {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await authFetch('/api/agent/video/endcard-upload', { method: 'POST', body: form });
  if (!res.ok) throw new Error(`endcard upload failed: ${res.status}`);
  const data = (await res.json()) as { data?: { filePath: string; fileName: string } };
  return { filePath: data.data?.filePath ?? '', fileName: data.data?.fileName ?? file.name };
}
